export type Theme = 'system' | 'light' | 'dark';

/**
 * Light or dark, once `system` has been resolved against the browser.
 *
 * The default is `system`, and the fallback under it is light: a planning tool
 * that opens dark on a machine set to light looks broken, and most people never
 * touch the toggle. An explicit choice is remembered and always wins.
 */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return prefersDark() ? 'dark' : 'light';
}

function prefersDark(): boolean {
  // matchMedia is missing in some test environments, and "no opinion" means light.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Watch the browser's setting, for as long as we are following it. Called with
 * the resolved theme whenever the system flips, so a machine that switches at
 * sunset takes the app with it.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/** What the toggle does next: the opposite of what is on screen, stated plainly. */
export function nextTheme(theme: Theme): 'light' | 'dark' {
  return resolveTheme(theme) === 'dark' ? 'light' : 'dark';
}
